/**
 * Engine Review API Service — Phase 07
 *
 * TypeScript client for the engine review REST endpoints.
 * Uses the existing `api` utility from utils/api.ts for auth + error handling.
 */

import { api } from '../utils/api';
import type {
  EngineId,
  EngineReviewStatus,
  EngineReviewListResponse,
  ApprovePayload,
  DisputePayload,
  RerunPayload,
  WaivePayload,
  OverridePayload,
  OverrideCreateResponse,
  OverrideInactivateResponse,
  ReportVerifierReport,
  FinalizePayload,
  FinalizeResponse,
} from '../types/engineReview';

// =============================================================================
// BASE PATH
// =============================================================================

const BASE = '/api/v1/engine-review';
const EXTERNAL_BASE = '/api/v1/external-accountant/review';

// =============================================================================
// READ OPERATIONS
// =============================================================================

/**
 * Get review status list for all 12 engines on a report.
 * Returns summary data suitable for the review landing page.
 */
export async function getEngineReviewList(reportId: string): Promise<EngineReviewListResponse> {
  return api.get<EngineReviewListResponse>(`${BASE}/${reportId}`);
}

/**
 * Get detailed review status for a single engine.
 * Includes verifier, consensus, overrides, history, and upstream provenance.
 */
export async function getEngineReview(
  reportId: string,
  engineId: EngineId,
): Promise<EngineReviewStatus> {
  return api.get<EngineReviewStatus>(`${BASE}/${reportId}/${engineId}`);
}

/**
 * Witness-CPA variant of `getEngineReviewList`. Phase 07.3n shipped the
 * matching backend route at `${EXTERNAL_BASE}/${reportId}` so the
 * AccountantEngineReviewLanding page renders in witness mode without
 * borrowing the in-house Clerk-gated endpoint (which 401/500s under a
 * witness JWT).
 */
export async function getEngineReviewListExternal(
  reportId: string,
): Promise<EngineReviewListResponse> {
  return api.get<EngineReviewListResponse>(`${EXTERNAL_BASE}/${reportId}`);
}

/**
 * Witness-CPA variant of `getEngineReview`. Phase 07.3n. Used by
 * `CalculationReviewPanel.fetchReviewStatus` when `mode === 'external'`
 * so the panel can mount under a witness JWT.
 */
export async function getEngineReviewExternal(
  reportId: string,
  engineId: EngineId,
): Promise<EngineReviewStatus> {
  return api.get<EngineReviewStatus>(`${EXTERNAL_BASE}/${reportId}/${engineId}`);
}

/**
 * Full report bundle for the in-house engine review surface — TS-FU12 fix.
 *
 * Used by `EngineDetailViews.useEngineReportData` when `mode !== 'external'`
 * so the 11 routed engines (everything except merchant_reconciliation)
 * can mount under a Clerk session. Pre-fix, the hook called the
 * witness-only `/api/v1/collaboration/reports/{id}/view` for ALL modes
 * → 401 "Invalid authentication token" for in-house users → entire
 * surface broke for those engines.
 *
 * Backend route declared BEFORE the parameterized `/{report_id}/{engine_id}`
 * route in engine_review.py so Starlette's literal-segment-first matching
 * picks it up. Loads from BOTH `drafts` and `reports` collections.
 */
export async function getReportViewInHouse(reportId: string): Promise<any> {
  return api.get<any>(`${BASE}/view/${reportId}`);
}

/**
 * Witness variant of `getReportViewInHouse`. Same shape; different namespace.
 * Used by `useEngineReportData` when `mode === 'external'`.
 */
export async function getReportViewExternal(reportId: string): Promise<any> {
  return api.get<any>(`/api/v1/collaboration/reports/${reportId}/view`);
}

// =============================================================================
// EXTERNAL ACCOUNTANT ACTIONS (JWT-auth routes)
// =============================================================================
// Phase 07.3b: approve / dispute / waive endpoints now backed by routes in
// backend/app/api/v1/external_accountant.py and gated by the witness-JWT auth
// dep `get_accountant_invited_to_report`. The `requestRerun` function below
// still 404s — witness rerun route was deferred per the 07.3b plan §Q3 pending
// a product decision on whether witness-CPAs can request reruns.

/**
 * Mark an engine as `under_review` from the witness-CPA portal.
 * Required first step before approve/dispute/waive can move the engine
 * out of `unreviewed` per SM1, mirroring `startReviewInHouse`.
 */
export async function startReview(
  reportId: string,
  engineId: EngineId,
  payload: { note?: string } = {},
): Promise<void> {
  await api.post(`${EXTERNAL_BASE}/${reportId}/${engineId}/start-review`, payload);
}

/**
 * Approve an engine as an external attester.
 */
export async function approveEngine(
  reportId: string,
  engineId: EngineId,
  payload: ApprovePayload,
): Promise<void> {
  await api.post(`${EXTERNAL_BASE}/${reportId}/${engineId}/approve`, payload);
}

/**
 * Dispute an engine as an external attester.
 * Routes the dispute to the in-house accountant queue.
 */
export async function disputeEngine(
  reportId: string,
  engineId: EngineId,
  payload: DisputePayload,
): Promise<void> {
  await api.post(`${EXTERNAL_BASE}/${reportId}/${engineId}/dispute`, payload);
}

// =============================================================================
// INITIAL-RUN — fires all 12 engines in DAG order on a fresh draft (2026-05-08)
// =============================================================================
//
// The trigger flows through the existing `Orchestrator` + `DispatcherService`
// substrate via `DispatchAction.RUN_CALCULATION_ENGINES` (added 2026-05-08),
// not a parallel endpoint. The dispatcher handles approval/queueing/audit
// uniformly with every other agent run.

/**
 * Response shape for `runInitialEngines` — mirrors the
 * `POST /orchestrator/cases/{report_id}/dispatch` response.
 */
export interface InitialRunResponse {
  success: boolean;
  dispatch_id: string;
  status: string;             // PENDING_APPROVAL | APPROVED | DISPATCHED ...
  action: string;             // "run_calculation_engines"
  auto_approved: boolean;     // True when Orchestrator.config.mode == FULL_AUTO
  message: string;
}

/**
 * Trigger an initial run of all 12 calculation engines via the existing
 * dispatcher. Creates a `dispatch_queue` record with action
 * `run_calculation_engines`; auto-approves if the orchestrator's
 * automation mode is FULL_AUTO, otherwise sits in PENDING_APPROVAL until
 * a privileged user approves it on the dispatch queue dashboard.
 *
 * Server-side: gated by the orchestrator's `get_current_user`. The
 * dispatcher's Celery task fans out to
 * `engine_pipeline_orchestrator.run_initial_engine_pipeline` which walks
 * the DAG and persists outputs.
 *
 * Idempotency: re-clicking creates additional dispatch records but each
 * produces a fresh `run_id` — `EngineOutputService.get_latest` selects
 * newest by timestamp, so older runs are shadowed.
 */
export async function runInitialEngines(
  reportId: string,
  priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' = 'MEDIUM',
): Promise<InitialRunResponse> {
  // The endpoint takes `action` and `priority` as query params, not body.
  const params = new URLSearchParams({
    action: 'run_calculation_engines',
    priority,
  });
  return api.post<InitialRunResponse>(
    `/api/v1/orchestrator/cases/${reportId}/dispatch?${params.toString()}`,
    {},
  );
}

/**
 * Request a rerun of an engine.
 * Optionally cascades to downstream engines.
 */
export async function requestRerun(
  reportId: string,
  engineId: EngineId,
  payload: RerunPayload,
): Promise<void> {
  await api.post(`${EXTERNAL_BASE}/${reportId}/${engineId}/rerun`, payload);
}

/**
 * Waive an engine (skip review with documented reason).
 */
export async function waiveEngine(
  reportId: string,
  engineId: EngineId,
  payload: WaivePayload,
): Promise<void> {
  await api.post(`${EXTERNAL_BASE}/${reportId}/${engineId}/waive`, payload);
}

// =============================================================================
// IN-HOUSE ACCOUNTANT ACTIONS (Clerk-auth routes)
// =============================================================================
// These hit /api/v1/engine-review/{id}/{engine}/{action}, which is the
// data_specialist-gated namespace shipped in PR #272. The Phase 07.3 retrofit
// surface (`mode === 'in_house_edit'`) dispatches through these.

/**
 * Add or update an override on an engine field (in-house only).
 *
 * Backend response shape is `OverrideCreateResponse` (see types/engineReview.ts),
 * NOT a bare `EngineOverride` — the response carries `override_id`,
 * cascade metadata, and rerun-task ids that the UI consumes for the
 * "downstream invalidated" feedback per Phase 07.6 plan.
 */
export async function addOverride(
  reportId: string,
  engineId: EngineId,
  payload: OverridePayload,
): Promise<OverrideCreateResponse> {
  return api.post<OverrideCreateResponse>(
    `${BASE}/${reportId}/${engineId}/override`,
    payload,
  );
}

/**
 * Inactivate an existing override (in-house only).
 *
 * Backend exposes this as DELETE on the override resource with a body
 * carrying the inactivation reason. Phase 07.6 fixed the prior client
 * which POSTed to a non-existent `/override/{id}/deactivate` path.
 *
 * Note: the api utility's standard `delete()` doesn't take a body in
 * many fetch wrappers — we use `request('DELETE', ...)` to thread the
 * `OverrideInactivateRequest` body through correctly.
 */
export async function deactivateOverride(
  reportId: string,
  engineId: EngineId,
  overrideId: string,
  reason: string,
): Promise<OverrideInactivateResponse> {
  return api.delete<OverrideInactivateResponse>(
    `${BASE}/${reportId}/${engineId}/override/${overrideId}`,
    { reason },
  );
}

/**
 * In-house mark engine as `under_review` (transition from `unreviewed`).
 * Required first step before approve/dispute/waive can move the engine
 * out of the unreviewed state per SM1.
 */
export async function startReviewInHouse(
  reportId: string,
  engineId: EngineId,
  payload: { note?: string } = {},
): Promise<void> {
  await api.post(`${BASE}/${reportId}/${engineId}/start-review`, payload);
}

/**
 * In-house approve (resolves a dispute, or completes a review).
 */
export async function approveEngineInHouse(
  reportId: string,
  engineId: EngineId,
  payload: ApprovePayload,
): Promise<void> {
  await api.post(`${BASE}/${reportId}/${engineId}/approve`, payload);
}

/**
 * In-house dispute (routes to the data-specialist queue with severity + fields).
 */
export async function disputeEngineInHouse(
  reportId: string,
  engineId: EngineId,
  payload: DisputePayload,
): Promise<void> {
  await api.post(`${BASE}/${reportId}/${engineId}/dispute`, payload);
}

/**
 * In-house waive (skip review with documented reason).
 */
export async function waiveEngineInHouse(
  reportId: string,
  engineId: EngineId,
  payload: WaivePayload,
): Promise<void> {
  await api.post(`${BASE}/${reportId}/${engineId}/waive`, payload);
}

/**
 * In-house request rerun (with cascade control).
 */
export async function requestRerunInHouse(
  reportId: string,
  engineId: EngineId,
  payload: RerunPayload,
): Promise<void> {
  await api.post(`${BASE}/${reportId}/${engineId}/rerun`, payload);
}

// =============================================================================
// POLLING / SSE HELPERS
// =============================================================================

/**
 * Poll for engine status updates.
 * Returns the current status for polling-based UI updates.
 */
export async function pollEngineStatus(
  reportId: string,
  engineId: EngineId,
): Promise<EngineReviewStatus> {
  return getEngineReview(reportId, engineId);
}

/**
 * Check if a rerun is still pending.
 * Convenience wrapper for polling rerun completion.
 */
export async function isRerunPending(
  reportId: string,
  engineId: EngineId,
): Promise<boolean> {
  const status = await getEngineReview(reportId, engineId);
  return status.status === 'rerun_pending';
}

// =============================================================================
// REPORT-LEVEL VERIFIER + EXTERNAL-ATTESTER FINALIZE
// =============================================================================

/**
 * Fetch the latest REPORT-LEVEL post-deliberation verifier report.
 * `GET /api/v1/war-room/verifier/{report_id}`. Returns the `run_id` the
 * attester must echo to finalize past an acknowledgment-required WARNING, plus
 * `checks_failed` (with severity). Throws (404) when no deliberation has yet
 * produced a report — callers treat that as "no warning to acknowledge".
 */
export async function getReportVerifier(
  reportId: string,
): Promise<ReportVerifierReport> {
  return api.get<ReportVerifierReport>(`/api/v1/war-room/verifier/${reportId}`);
}

/**
 * Finalize (attest) a report from the external-attester portal.
 * `POST /api/v1/external-accountant/review/{report_id}/finalize`.
 * When the latest verifier report raises an acknowledgment-required WARNING
 * (e.g. `endorsement_grounding`), `acknowledged_verifier_run_id` MUST equal
 * that report's `run_id` or the backend 412-gates the finalize.
 */
export async function finalizeAttestation(
  reportId: string,
  payload: FinalizePayload,
): Promise<FinalizeResponse> {
  return api.post<FinalizeResponse>(
    `${EXTERNAL_BASE}/${reportId}/finalize`,
    payload,
  );
}
// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  getReportVerifier,
  finalizeAttestation,
  getEngineReviewList,
  getEngineReview,
  getEngineReviewListExternal,
  getEngineReviewExternal,
  startReview,
  approveEngine,
  disputeEngine,
  requestRerun,
  waiveEngine,
  addOverride,
  deactivateOverride,
  startReviewInHouse,
  approveEngineInHouse,
  disputeEngineInHouse,
  waiveEngineInHouse,
  requestRerunInHouse,
  pollEngineStatus,
  isRerunPending,
};
